import type { AttackProfile, Skill } from '../content/types.ts';
import type { Character } from '../domain/character.ts';
import type { CustomSkillLibrary } from '../domain/customSkill.ts';
import type { CustomSkillDraft, CustomSkillProblem } from '../rules/customSkills.ts';
import {
  applyCustomSkillDefinitionEdit,
  buildCustomSkillDefinition,
  checkCustomSkillDraft,
  definitionDraftFromSkill,
} from '../rules/customSkills.ts';
import type { CustomSkillRepository, LibraryStatus } from '../persistence/customSkillRepository.ts';
import type { CustomBackgroundRepository } from '../persistence/customBackgroundRepository.ts';
import type { CustomItemRepository } from '../persistence/customItemRepository.ts';
import { describeReferences, findContentReferences } from './customContentUsage.ts';
import type { CharacterRepository } from '../persistence/characterRepository.ts';
import {
  contentRegistry,
  setCustomSkillContent,
} from '../content/dccCoreContent.ts';
import { newId } from './ids.ts';
import type { IdAllocatorDeps } from './customContentIds.ts';
import { allocateCustomId } from './customContentIds.ts';
import { weaponLinkProblem } from '../rules/inventory.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import { checkCharacterIntegrity } from './characterIntegrity.ts';
import { collectReversibleReferences } from '../portable/customDependencies.ts';

/**
 * Canonical live backend for player-authored Skills and Spells.
 *
 * Skill and optional AttackProfile are one logical definition, stored in one
 * custom Skill library JSON record and installed into the one live registry.
 * Character-owned Rank never enters this service.
 */
export interface CustomSkillDeps extends IdAllocatorDeps {
  customSkills: CustomSkillRepository;
  customBackgrounds: CustomBackgroundRepository;
  customItems: CustomItemRepository;
  characters: CharacterRepository;
  now: () => string;
}

export type CustomSkillOutcome =
  | { ok: true; library: CustomSkillLibrary; skill: Skill; attack: AttackProfile | null }
  | { ok: false; problems: CustomSkillProblem[] };

export async function loadCustomSkillsIntoCatalogue(deps: {
  customSkills: CustomSkillRepository;
}): Promise<{ library: CustomSkillLibrary; problems: string[]; status: LibraryStatus }> {
  const { library, problems, status } = await deps.customSkills.load();
  const rejected = setCustomSkillContent(library.skills, library.attacks);
  const all = [...problems];
  for (const { item, reason } of rejected) {
    all.push(`Custom Skill "${item.name}" was not loaded: ${reason}.`);
  }
  if (status === 'unreadable') {
    all.push(
      'Your custom Skill file has been left exactly as it is. Crawlers keep the Skills and ' +
        'Ranks they had; those Skills will not appear until the file can be read.',
    );
  }
  return { library, problems: all, status };
}

export async function createCustomSkill(
  library: CustomSkillLibrary,
  draft: CustomSkillDraft,
  deps: CustomSkillDeps,
): Promise<CustomSkillOutcome> {
  const problems = checkCustomSkillDraft(draft, contentRegistry.allSkills(), null);
  if (problems.length > 0) return { ok: false, problems };

  const allocated = await allocateCustomId(draft.name, deps, newId);
  if (!allocated.ok) return { ok: false, problems: [{ field: 'name', message: allocated.message }] };

  const definition = buildCustomSkillDefinition(draft, allocated.id, deps.now());
  const saved = await deps.customSkills.saveDefinitions(
    library,
    [...library.skills, definition.skill],
    definition.attack === null ? library.attacks : [...library.attacks, definition.attack],
  );
  setCustomSkillContent(saved.skills, saved.attacks);
  return { ok: true, library: saved, skill: definition.skill, attack: definition.attack };
}

/** A crawler loaded while checking a structural edit. */
interface InspectedCrawler {
  character: Character;
}

async function inspectAllCrawlers(
  characters: CharacterRepository,
): Promise<{ ok: true; crawlers: InspectedCrawler[] } | { ok: false; ids: string[] }> {
  let storedIds: string[];
  try {
    storedIds = await characters.listStoredIds();
  } catch {
    return { ok: false, ids: ['character storage'] };
  }
  const crawlers: InspectedCrawler[] = [];
  const ids: string[] = [];
  for (const id of storedIds) {
    try {
      crawlers.push({ character: await characters.load(id) });
    } catch {
      ids.push(id);
    }
  }
  return ids.length > 0 ? { ok: false, ids } : { ok: true, crawlers };
}

/**
 * Structural Skill edits are definition-only changes that can invalidate saved
 * Character state without touching the Character record. They fail closed.
 */
async function structuralSkillProblems(
  before: Skill,
  after: Skill,
  characters: CharacterRepository,
): Promise<CustomSkillProblem[]> {
  const spellKindChanged = before.kind === 'spell' && after.kind !== 'spell';
  // Character records can legally hold Ranks through 20. An absent definition
  // ceiling therefore behaves as 20 for this safety question. Only a STRICTLY
  // lower authored ceiling can invalidate already-saved crawler Ranks. Raising
  // or removing a ceiling is non-structural and must not force a crawler scan.
  const previousCeiling = before.maxRank ?? 20;
  const maxRankNarrowed = after.maxRank !== undefined && after.maxRank < previousCeiling;
  if (!spellKindChanged && !maxRankNarrowed) return [];

  const inspected = await inspectAllCrawlers(characters);
  if (!inspected.ok) {
    const field: CustomSkillProblem['field'] = spellKindChanged ? 'kind' : 'maxRank';
    return [
      {
        field,
        message:
          'Nothing has been changed. Crawler files required for this structural edit could not ' +
          `be inspected: ${inspected.ids.join(', ')}. Repair or remove them first.`,
      },
    ];
  }

  const problems: CustomSkillProblem[] = [];
  if (spellKindChanged) {
    const hotlisted = inspected.crawlers
      .map((c) => c.character)
      .filter((character) =>
        character.hotlist.some(
          (slot) => slot?.kind === 'spell' && slot.skillId === before.id,
        ),
      )
      .map((character) => character.name);
    if (hotlisted.length > 0) {
      problems.push({
        field: 'kind',
        message:
          `Cannot change this Spell into a non-Spell while it is in the Hotlist for ${hotlisted.join(', ')}. ` +
          'Remove it from those Hotlists first. Nothing has been changed.',
      });
    }
  }

  if (maxRankNarrowed && after.maxRank !== undefined) {
    const tooHigh = inspected.crawlers
      .map((c) => c.character)
      .map((character) => ({
        character,
        rank: character.skills.find((s) => s.skillId === before.id)?.rank,
      }))
      .filter((entry): entry is { character: Character; rank: number } => entry.rank !== undefined)
      .filter((entry) => entry.rank > (after.maxRank as number));
    if (tooHigh.length > 0) {
      problems.push({
        field: 'maxRank',
        message:
          `Cannot set Max Rank to ${after.maxRank}: ` +
          tooHigh.map((e) => `${e.character.name} is Rank ${e.rank}`).join(', ') +
          '. Lower their Rank explicitly first. Nothing has been changed.',
      });
    }
  }

  return problems;
}

type ProfileStructuralRisk = 'weapon-link' | 'hand-occupancy' | null;

/**
 * Which AttackProfile edit can indirectly change the structure of linked Items.
 *
 * Removing a profile, or turning a held weapon into a non-weapon, can break the
 * Item -> Attack relationship outright. Changing `weaponUse.hands` leaves that
 * relationship valid but changes the Item's effective hand occupancy through
 * the shared `heldSlotsFor` authority, so saved crawler layouts must be checked
 * as if the Item itself had changed structurally.
 */
function profileStructuralRisk(
  before: AttackProfile | null,
  after: AttackProfile | null,
): ProfileStructuralRisk {
  if (before === null) return null;
  if (after === null) return 'weapon-link';
  if (before.weaponUse !== null && after.weaponUse === null) return 'weapon-link';
  if (
    before.weaponUse !== null &&
    after.weaponUse !== null &&
    before.weaponUse.hands !== after.weaponUse.hands
  ) {
    return 'hand-occupancy';
  }
  return null;
}

async function profileDependencyProblem(
  skillId: string,
  before: AttackProfile | null,
  after: AttackProfile | null,
  deps: Pick<CustomSkillDeps, 'customItems' | 'characters'>,
): Promise<CustomSkillProblem | null> {
  const risk = profileStructuralRisk(before, after);
  if (risk === null) return null;

  // A profile edit can change the meaning of an Item without changing the Item
  // record itself. The Item library is therefore part of the structural safety
  // boundary and must be readable before we can decide whether any dependency
  // exists.
  const loaded = await deps.customItems.load();
  if (loaded.status === 'unreadable') {
    return {
      field: 'attack',
      message:
        'Nothing has been changed. Your custom Item file cannot be read, so there is no way to ' +
        'tell whether an Item depends on this AttackProfile. Repair that file first.',
    };
  }

  const dependent = loaded.library.items.filter((item) => item.weaponAttackId === skillId);
  if (dependent.length === 0) return null;

  const storedItems = new Map(loaded.library.items.map((item) => [item.id, item]));
  const proposed: InventoryCatalogue = {
    lookupItem: (id) => storedItems.get(id) ?? contentRegistry.getItem(id),
    lookupAttack: (id) =>
      id === skillId ? (after ?? undefined) : contentRegistry.getAttack(id),
  };

  // Profile removal / held-weapon -> null stays protected by the existing
  // relationship authority. The hand-occupancy guard below supplements this; it
  // does not replace it.
  const brokenLinks = dependent.filter((item) => weaponLinkProblem(item, proposed) !== null);
  if (brokenLinks.length > 0) {
    return {
      field: 'attack',
      message:
        `Cannot change this AttackProfile because ${brokenLinks.map((i) => i.name).join(', ')} ` +
        `${brokenLinks.length === 1 ? 'references' : 'reference'} it as a held weapon. ` +
        'Change those custom Items first. Nothing has been changed.',
    };
  }

  if (
    risk !== 'hand-occupancy' ||
    before === null ||
    after === null ||
    before.weaponUse === null ||
    after.weaponUse === null
  ) {
    return null;
  }
  const beforeWeaponUse = before.weaponUse;
  const afterWeaponUse = after.weaponUse;

  // RULE DCC-CUSTOM-013 applies even when the structural change arrives
  // INDIRECTLY through the linked AttackProfile. Storage-authoritative
  // enumeration is mandatory: a rebuilt convenience index can omit a corrupt
  // crawler and must never turn an unsafe edit into an apparent success.
  const inspected = await inspectAllCrawlers(deps.characters);
  if (!inspected.ok) {
    return {
      field: 'attack',
      message:
        'Nothing has been changed. Crawler files required for this structural AttackProfile edit ' +
        `could not be inspected: ${inspected.ids.join(', ')}. Repair or remove them first.`,
    };
  }

  const dependentIds = new Set(dependent.map((item) => item.id));
  const inHistory = inspected.crawlers
    .map((entry) => entry.character)
    .filter((character) => {
      const reversible = collectReversibleReferences(character);
      return [...dependentIds].some((itemId) => reversible.has(itemId));
    });
  if (inHistory.length > 0) {
    return {
      field: 'attack',
      message:
        `${inHistory.map((character) => character.name).join(', ')} can still undo a change involving ` +
        `an Item whose hand requirement comes from this AttackProfile. That structural behavior is frozen ` +
        'until the reversible history ages out. Nothing has been changed.',
    };
  }

  // Reducing a held weapon from two hands to one cannot make the CURRENT layout
  // worse. History is still frozen above, but no arithmetic-based current-state
  // refusal is needed in this direction.
  if (afterWeaponUse.hands <= beforeWeaponUse.hands) return null;

  const brokenCrawlers = inspected.crawlers
    .map((entry) => entry.character)
    .filter((character) =>
      character.inventory.some(
        (entry) => dependentIds.has(entry.itemId) && (entry.equipped > 0 || entry.held > 0),
      ),
    )
    .map((character) => ({ character, report: checkCharacterIntegrity(character, proposed) }))
    .filter((entry) => !entry.report.ok);

  if (brokenCrawlers.length > 0) {
    return {
      field: 'attack',
      message:
        'Cannot increase this AttackProfile hand requirement because it would leave ' +
        brokenCrawlers
          .map(({ character, report }) =>
            `${character.name} (${report.problems[0] ?? 'invalid inventory'})`,
          )
          .join(', ') +
        ' with equipment they cannot have. Unequip something on those crawlers first. Nothing has been changed.',
    };
  }

  return null;
}

export async function editCustomSkill(
  library: CustomSkillLibrary,
  skillId: string,
  draft: CustomSkillDraft,
  deps: CustomSkillDeps,
): Promise<CustomSkillOutcome> {
  const existing = library.skills.find((s) => s.id === skillId);
  if (!existing) {
    return { ok: false, problems: [{ field: 'id', message: 'That custom Skill no longer exists.' }] };
  }

  const problems = checkCustomSkillDraft(draft, contentRegistry.allSkills(), skillId);
  if (problems.length > 0) return { ok: false, problems };

  const existingAttack = library.attacks.find((a) => a.id === skillId) ?? null;
  const updated = applyCustomSkillDefinitionEdit(existing, existingAttack, draft, deps.now());

  const structural = await structuralSkillProblems(existing, updated.skill, deps.characters);
  if (structural.length > 0) return { ok: false, problems: structural };

  const itemProblem = await profileDependencyProblem(skillId, existingAttack, updated.attack, deps);
  if (itemProblem !== null) return { ok: false, problems: [itemProblem] };

  const skills = library.skills.map((s) => (s.id === skillId ? updated.skill : s));
  const attacks = library.attacks.filter((a) => a.id !== skillId);
  if (updated.attack !== null) attacks.push(updated.attack);

  const saved = await deps.customSkills.saveDefinitions(library, skills, attacks);
  setCustomSkillContent(saved.skills, saved.attacks);
  return { ok: true, library: saved, skill: updated.skill, attack: updated.attack };
}

export interface SkillUsage {
  characterId: string;
  characterName: string;
  rank: number;
}

export interface UsageScan {
  usage: SkillUsage[];
  uninspectable: { characterId: string; reason: string }[];
}

export async function findSkillUsage(
  skillId: string,
  characters: CharacterRepository,
): Promise<UsageScan> {
  const ids = await characters.listStoredIds();
  const usage: SkillUsage[] = [];
  const uninspectable: { characterId: string; reason: string }[] = [];

  for (const id of ids) {
    let character: Character;
    try {
      character = await characters.load(id);
    } catch (err) {
      uninspectable.push({
        characterId: id,
        reason: err instanceof Error ? err.message : String(err),
      });
      continue;
    }
    const held = character.skills.find((s) => s.skillId === skillId);
    if (held) usage.push({ characterId: character.id, characterName: character.name, rank: held.rank });
  }
  return { usage, uninspectable };
}

export interface BackgroundDependency {
  backgroundId: string;
  backgroundName: string;
}

export interface ItemDependency {
  itemId: string;
  itemName: string;
}

export type DeleteOutcome =
  | { ok: true; library: CustomSkillLibrary }
  | {
      ok: false;
      inUseBy: SkillUsage[];
      uninspectable: { characterId: string; reason: string }[];
      requiredBy: BackgroundDependency[];
      requiredByItems: ItemDependency[];
      message: string;
    };

export async function deleteCustomSkill(
  library: CustomSkillLibrary,
  skillId: string,
  deps: CustomSkillDeps,
): Promise<DeleteOutcome> {
  const { references, uninspectable } = await findContentReferences(skillId, deps.characters);
  const usage: SkillUsage[] = references.map((r) => ({
    characterId: r.characterId,
    characterName: r.characterName,
    rank: r.character.skills.find((sk) => sk.skillId === skillId)?.rank ?? 0,
  }));

  const [backgroundLibrary, itemLibrary] = await Promise.all([
    deps.customBackgrounds.load(),
    deps.customItems.load(),
  ]);

  if (backgroundLibrary.status === 'unreadable' || itemLibrary.status === 'unreadable') {
    const what = [
      ...(backgroundLibrary.status === 'unreadable' ? ['custom Background'] : []),
      ...(itemLibrary.status === 'unreadable' ? ['custom Item'] : []),
    ].join(' and ');
    return {
      ok: false,
      inUseBy: usage,
      uninspectable,
      requiredBy: [],
      requiredByItems: [],
      message:
        `Nothing has been deleted. Your ${what} file could not be read, so dependencies on this ` +
        'Skill/AttackProfile cannot be ruled out. Repair that file first.',
    };
  }

  const requiredBy: BackgroundDependency[] = backgroundLibrary.library.backgrounds
    .filter((b) => b.skillIds.includes(skillId))
    .map((b) => ({ backgroundId: b.id, backgroundName: b.name }));
  const requiredByItems: ItemDependency[] = itemLibrary.library.items
    .filter(
      (i) =>
        i.weaponAttackId === skillId ||
        i.modifiers.some(
          (modifier) =>
            modifier.target.kind === 'skill-rank' && modifier.target.skillId === skillId,
        ),
    )
    .map((i) => ({ itemId: i.id, itemName: i.name }));

  if (uninspectable.length > 0) {
    return {
      ok: false,
      inUseBy: usage,
      uninspectable,
      requiredBy,
      requiredByItems,
      message:
        `Nothing has been deleted. ${uninspectable.length} crawler` +
        `${uninspectable.length === 1 ? '' : 's'} could not be opened, so there is no way to ` +
        `tell whether they use this Skill: ${uninspectable.map((u) => u.characterId).join(', ')}.`,
    };
  }

  if (requiredBy.length > 0) {
    const names = requiredBy.map((b) => b.backgroundName).join(', ');
    return {
      ok: false,
      inUseBy: usage,
      uninspectable: [],
      requiredBy,
      requiredByItems,
      message:
        `This Skill is offered by your Background${requiredBy.length === 1 ? '' : 's'} ${names}. ` +
        'Change the Background first, then delete the Skill. Nothing has been changed.',
    };
  }

  if (requiredByItems.length > 0) {
    return {
      ok: false,
      inUseBy: usage,
      uninspectable: [],
      requiredBy: [],
      requiredByItems,
      message:
        `Cannot delete this Skill because ${requiredByItems.map((i) => i.itemName).join(', ')} ` +
        `${requiredByItems.length === 1 ? 'depends' : 'depend'} on it through a weapon link or ` +
        'Skill Rank modifier. Change those custom Items first. Nothing has been changed.',
    };
  }

  if (usage.length > 0) {
    return {
      ok: false,
      inUseBy: usage,
      uninspectable: [],
      requiredBy: [],
      requiredByItems: [],
      message:
        `${describeReferences('this Skill', references)} ` +
        'Remove it in Edit Character first, then delete it here.',
    };
  }

  const saved = await deps.customSkills.saveDefinitions(
    library,
    library.skills.filter((s) => s.id !== skillId),
    library.attacks.filter((a) => a.id !== skillId),
  );
  setCustomSkillContent(saved.skills, saved.attacks);
  return { ok: true, library: saved };
}

/**
 * Copies reusable definition data under a fresh global custom identity.
 * Character Rank is not part of either input, so it cannot accidentally copy.
 */
export async function duplicateCustomSkill(
  library: CustomSkillLibrary,
  sourceSkillId: string,
  newName: string,
  deps: CustomSkillDeps,
): Promise<CustomSkillOutcome> {
  const sourceSkill = library.skills.find((s) => s.id === sourceSkillId) ?? contentRegistry.getSkill(sourceSkillId);
  if (!sourceSkill) {
    return { ok: false, problems: [{ field: 'id', message: 'That source Skill does not exist.' }] };
  }

  // Custom schema intentionally cannot enforce core-only restrictions. Turning
  // one into decorative notes would silently weaken mechanics.
  if (sourceSkill.restriction !== null) {
    return {
      ok: false,
      problems: [
        {
          field: 'id',
          message:
            `Cannot duplicate ${sourceSkill.name} faithfully because it has the enforced ` +
            `restriction "${sourceSkill.restriction}", which custom Skills cannot represent.`,
        },
      ],
    };
  }

  // Damage Effects are separate executable content keyed to the original Skill
  // ID. The custom Skill library has no DamageEffect companion collection, so a
  // copy would lose real mechanics even though the AttackProfile itself copied.
  const effects = contentRegistry.damageEffectsFor(sourceSkillId);
  if (effects.length > 0) {
    return {
      ok: false,
      problems: [
        {
          field: 'attack',
          message:
            `Cannot duplicate ${sourceSkill.name} faithfully because it has executable Damage ` +
            'Effects that the custom Skill schema cannot carry.',
        },
      ],
    };
  }

  const sourceAttack =
    library.attacks.find((a) => a.id === sourceSkillId) ?? contentRegistry.getAttack(sourceSkillId) ?? null;
  const draft = { ...definitionDraftFromSkill(sourceSkill, sourceAttack), name: newName };
  const problems = checkCustomSkillDraft(draft, contentRegistry.allSkills(), null);
  if (problems.length > 0) return { ok: false, problems };

  const allocated = await allocateCustomId(newName, deps, newId);
  if (!allocated.ok) return { ok: false, problems: [{ field: 'name', message: allocated.message }] };

  const definition = buildCustomSkillDefinition(draft, allocated.id, deps.now());
  const saved = await deps.customSkills.saveDefinitions(
    library,
    [...library.skills, definition.skill],
    definition.attack === null ? library.attacks : [...library.attacks, definition.attack],
  );
  setCustomSkillContent(saved.skills, saved.attacks);
  return { ok: true, library: saved, skill: definition.skill, attack: definition.attack };
}
